import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Header from '@/components/Header';
import PageHero from '@/components/PageHero';
import { LinkButton } from '@/components/Button';
import { 
  Search, 
  ChevronDown, 
  ChevronUp,
  Home,
  User,
  HelpCircle,
  Settings,
  Shield,
  Briefcase,
  DollarSign,
  FileText
} from 'lucide-react';

// Define types for our help content
type Article = {
  id: string;
  title: string;
  content: string;
  icon?: React.ReactNode;
};

type HelpCategory = {
  id: string;
  title: string;
  icon: React.ReactNode;
  articles: Article[];
};

// Help Categories Data
const helpCategories: HelpCategory[] = [
  {
    id: 'getting-started',
    title: 'Getting Started',
    icon: <Home className="h-6 w-6" />,
    articles: [
      {
        id: 'what-is-altax',
        title: 'What is ALTA-X?',
        content: 'ALTA-X is a premier investment platform that provides accredited investors with exclusive access to curated alternative investment opportunities in private markets, such as real estate, private equity, and venture capital.',
      },
      {
        id: 'how-to-join',
        title: 'How do I sign up?',
        content: 'You can create an account by clicking the "Sign Up" button on our homepage. The process is quick, secure, and requires you to provide basic information to verify your identity and accreditation status.',
      },
      {
        id: 'who-can-invest',
        title: 'Who is eligible to invest?',
        content: 'Our platform is designed for accredited investors. An accredited investor is an individual who meets specific income or net worth requirements as defined by financial regulations. This ensures that our investors have the financial sophistication to understand the risks and potential rewards of alternative investments.',
      },
    ],
  },
  {
    id: 'investing',
    title: 'Investing with ALTA-X',
    icon: <Briefcase className="h-6 w-6" />,
    articles: [
      {
        id: 'investment-process',
        title: 'What is the investment process like?',
        content: 'Our process is simple: 1. Browse our curated deals. 2. Review the detailed investment memorandum and due diligence documents. 3. Complete the investment and sign documents electronically. 4. Fund your investment through our secure portal. 5. Track your investment performance on your dashboard.',
      },
      {
        id: 'investment-minimums',
        title: 'What are the minimum investment amounts?',
        content: 'Minimum investment amounts vary by opportunity but are clearly stated on each deal page. We strive to provide opportunities with accessible entry points, often starting from R50,000.',
      },
      {
        id: 'what-are-the-fees',
        title: 'What are the fees?',
        content: 'ALTA-X charges a one-time administration fee and an annual management fee, which are clearly outlined in the investment documents for each opportunity. We believe in full transparency with no hidden costs.',
      },
    ],
  },
  {
    id: 'security',
    title: 'Security & Trust',
    icon: <Shield className="h-6 w-6" />,
    articles: [
      {
        id: 'is-my-data-secure',
        title: 'Is my personal and financial information secure?',
        content: 'Yes. We use bank-level encryption (SSL) and robust security protocols to protect your data. We are committed to maintaining the highest standards of privacy and security.',
      },
      {
        id: 'due-diligence',
        title: 'How does ALTA-X vet investments?',
        content: 'Our expert team conducts a rigorous due diligence process on every potential investment. This includes financial analysis, market research, management team evaluation, and legal review to ensure every opportunity meets our stringent quality standards.',
      },
      {
        id: 'risks',
        title: 'What are the risks involved?',
        content: 'All investments carry risk, including the potential loss of principal. Alternative investments are typically illiquid and subject to market, credit, and operational risks. We provide comprehensive risk factors for each deal to help you make informed decisions.',
      },
    ],
  },
];

const AccordionItem: React.FC<{ category: HelpCategory; article: Article; isActive: boolean; onClick: () => void }> = ({ category, article, isActive, onClick }) => {
  return (
    <div className="border-b border-gray-200">
      <button
        className="w-full text-left py-5 px-6 flex justify-between items-center hover:bg-gray-50 focus:outline-none"
        onClick={onClick}
      >
        <span className="text-lg font-medium text-gray-800">{article.title}</span>
        {isActive ? <ChevronUp className="h-6 w-6 text-gray-500" /> : <ChevronDown className="h-6 w-6 text-gray-500" />}
      </button>
      {isActive && (
        <div className="px-6 pb-5 text-gray-600 leading-relaxed">
          {article.content}
        </div>
      )}
    </div>
  );
};

const HelpCenter: React.FC = () => {
  const [expandedCategories, setExpandedCategories] = useState<Record<string, boolean>>({});
  const [activeCategory, setActiveCategory] = useState<string>('');
  const [activeArticle, setActiveArticle] = useState<string>('');
  const [searchQuery, setSearchQuery] = useState('');
  
  const navigate = useNavigate();
  const location = useLocation();

  const toggleCategory = (categoryId: string): void => {
    setExpandedCategories(prev => ({
      ...prev,
      [categoryId]: !prev[categoryId]
    }));
  };

  const handleArticleClick = (categoryId: string, articleId: string): void => {
    const newActiveArticle = activeArticle === articleId ? '' : articleId;
    setActiveArticle(newActiveArticle);
    setActiveCategory(categoryId);

    if (newActiveArticle) {
      navigate(`#${categoryId}_${articleId}`, { replace: true });
    } else {
      navigate(location.pathname, { replace: true });
    }
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    setSearchQuery(e.target.value);
  };

  useEffect(() => {
    const hash = location.hash.replace('#', '');
    if (hash) {
      const [categoryId, articleId] = hash.split('_');
      if (categoryId && articleId) {
        setActiveCategory(categoryId);
        setActiveArticle(articleId);
        setExpandedCategories(prev => ({ ...prev, [categoryId]: true }));
      }
    }
  }, [location]);

  const filteredCategories = helpCategories
    .map(category => ({
      ...category,
      articles: category.articles.filter(article => 
        article.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        article.content.toLowerCase().includes(searchQuery.toLowerCase())
      )
    }))
    .filter(category => category.articles.length > 0);

  const renderArticleContent = (content: string): React.ReactNode => {
    if (!content) return null;
    
    const paragraphs = content.split('\n\n');
    return paragraphs.map((paragraph, i) => {
      if (paragraph.startsWith('#')) {
        const level = paragraph.match(/^#+/) ? paragraph.match(/^#+/)![0].length : 1;
        const text = paragraph.replace(/^#+\s*/, '');
        return React.createElement(`h${Math.min(level + 1, 6)}`, { 
          key: i, 
          className: 'font-bold mt-4 mb-2' 
        }, text);
      } else if (paragraph.startsWith('- ')) {
        const items = paragraph.split('\n').filter(Boolean);
        return (
          <ul key={i} className="list-disc pl-5 mb-4 space-y-1">
            {items.map((item, idx) => (
              <li key={idx}>{item.replace(/^-\s*/, '')}</li>
            ))}
          </ul>
        );
      } else {
        return <p key={i} className="mb-4">{paragraph}</p>;
      }
    });
  };

  return (
    <div className="bg-white min-h-screen">
      <main>
        <PageHero
          title="How can we help?"
          subtitle="Find answers to your questions about investing with ALTA-X. Search our knowledge base or browse categories below."
          backgroundImage="https://images.unsplash.com/photo-1516321497487-e288fb19713f?q=80&w=2070&auto=format&fit=crop"
        >
          <div className="mt-8 max-w-xl w-full">
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                <Search className="h-5 w-5 text-gray-400" />
              </div>
              <input
                type="search"
                name="search"
                id="search"
                value={searchQuery}
                onChange={handleSearchChange}
                className="block w-full pl-12 pr-4 py-3 border border-gray-300 rounded-full bg-white/90 backdrop-blur-sm placeholder-gray-500 focus:ring-2 focus:ring-[hsl(var(--bronze-gold))] focus:border-[hsl(var(--bronze-gold))] transition"
                placeholder="Search for answers..."
              />
            </div>
          </div>
        </PageHero>

        <section className="py-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            {filteredCategories.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                {filteredCategories.map(category => (
                  <div key={category.id} className="bg-gray-50 rounded-xl p-8 border border-gray-100">
                    <div className="flex items-center text-[hsl(var(--navy))] mb-4">
                      {category.icon}
                      <h3 className="text-2xl font-bold ml-4 text-gray-900">{category.title}</h3>
                    </div>
                    <div className="space-y-2 mt-6">
                      {category.articles.map(article => (
                        <a 
                          href={`#${category.id}_${article.id}`}
                          key={article.id}
                          onClick={(e) => { e.preventDefault(); handleArticleClick(category.id, article.id); }}
                          className='block font-medium text-gray-700 hover:text-[hsl(var(--bronze-gold))] transition-colors duration-200'
                        >
                          {article.title}
                        </a>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 sm:py-12">
                <HelpCircle className="mx-auto h-12 w-12 text-gray-400" />
                <h3 className="mt-2 text-lg font-medium text-gray-900">
                  No results found
                </h3>
                <p className="mt-1 text-gray-500 text-sm sm:text-base">
                  Try searching for something else or browse our categories.
                </p>
              </div>
            )}
          </div>
        </section>

        {/* Main content area with selected article */}
        <section className="pb-16 md:pb-24">
          <div className="container mx-auto px-4">
            <div className="bg-white rounded-lg shadow p-6 md:p-8">
              {activeArticle ? (
                <article className="prose max-w-none">
                  <h1 className="text-xl sm:text-2xl font-bold mb-4">
                    {helpCategories
                      .flatMap((c) => c.articles)
                      .find((a) => a.id === activeArticle)?.title}
                  </h1>
                  <div className="text-sm sm:text-base">
                    {renderArticleContent(
                      helpCategories
                        .flatMap((c) => c.articles)
                        .find((a) => a.id === activeArticle)?.content || ''
                    )}
                  </div>
                </article>
              ) : (
                <div className="text-center py-8 sm:py-12">
                  <HelpCircle className="mx-auto h-12 w-12 text-gray-400" />
                  <h3 className="mt-2 text-lg font-medium text-gray-900">
                    Welcome to ALTA-X Help Center
                  </h3>
                  <p className="mt-1 text-gray-500 text-sm sm:text-base">
                    Select a category and article to get started, or use the search bar to find
                    specific information.
                  </p>
                </div>
              )}
            </div>
          </div>
        </section>
      </main>
    </div>
  );
};

export default HelpCenter;
